import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  CheckCircle2,
  MessageCircle,
} from "lucide-react";

/**
 * PresentationFlowReplica
 * Réplica navegable (solo demo, sin backend) del WhatsApp Flow "Solicitar presentación",
 * tal cual lo verá el lead en su móvil. Cuatro pantallas: modalidad → día/hora → nombre del
 * negocio → confirmación. La MODALIDAD (presencial/online) se pregunta lo primero de todo.
 * El día se elige con un CALENDAR PICKER (vista de mes), como en el flow real; findes y
 * días pasados quedan deshabilitados (demo). Horas simuladas. 100% responsive.
 */
const MOCK_HOURS = ["09:00", "09:30", "10:00", "10:30", "11:00", "12:00", "16:00", "16:30", "17:00"];
const WD = ["L", "M", "X", "J", "V", "S", "D"];
const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtEs(iso) {
  if (!iso) return "";
  const [y, m, dd] = iso.split("-");
  return `${dd}/${m}/${y}`;
}

/** Calendar picker mensual. Deshabilita pasados, findes y > +30 días (demo). */
function MonthCalendar({ selected, onSelect }) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const maxDate = useMemo(() => new Date(today.getTime() + 30 * 86400000), [today]);
  const [view, setView] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const year = view.getFullYear();
  const month = view.getMonth();
  const startWeekday = (new Date(year, month, 1).getDay() + 6) % 7; // lunes = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const canPrev = year > today.getFullYear() || month > today.getMonth();
  const canNext = new Date(year, month, 1) < new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

  const dayState = (d) => {
    const date = new Date(year, month, d);
    date.setHours(0, 0, 0, 0);
    const dow = date.getDay();
    const disabled = date < today || date > maxDate || dow === 0 || dow === 6;
    return { iso: isoOf(date), disabled };
  };

  return (
    <div className="rounded-2xl border border-slate-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          disabled={!canPrev}
          onClick={() => setView(new Date(year, month - 1, 1))}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 disabled:opacity-30 hover:bg-slate-100 active:scale-90"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-bold text-slate-700 capitalize">
          {MONTHS[month]} {year}
        </span>
        <button
          type="button"
          disabled={!canNext}
          onClick={() => setView(new Date(year, month + 1, 1))}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 disabled:opacity-30 hover:bg-slate-100 active:scale-90"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WD.map((w, i) => (
          <div key={i} className="text-center text-[10px] font-black text-slate-400 uppercase py-1">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d == null) return <div key={i} />;
          const { iso, disabled } = dayState(d);
          const isSel = selected === iso;
          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(iso)}
              className={`aspect-square rounded-xl text-xs font-bold transition-all ${
                isSel
                  ? "bg-emerald-500 text-white shadow-md shadow-emerald-200/50"
                  : disabled
                    ? "text-slate-300 cursor-not-allowed"
                    : "text-slate-700 hover:bg-emerald-50 active:scale-90"
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function PresentationFlowReplica({
  open,
  onClose,
  vendorName = "tu comercial",
  online = false,
  // Se abre desde dentro de FlowPreviewModal, que a su vez puede abrirse desde
  // SendFlowModal: el z-index tiene que ir subiendo por la cadena o queda detrás.
  overlayZIndex = 70,
}) {
  // En televenta (online) el flow real NO pregunta modalidad: va directo a la
  // videollamada. Saltamos la pantalla "modalidad" y fijamos modalidad="online".
  const [screen, setScreen] = useState(online ? "slot" : "modalidad"); // modalidad | slot | details | success
  const [dayIso, setDayIso] = useState(null);
  const [hour, setHour] = useState(null);
  const [business, setBusiness] = useState("");
  const [modalidad, setModalidad] = useState(online ? "online" : null); // "presencial" | "online"

  // Al abrir, (re)inicializa la pantalla según `online`: el initializer de useState
  // solo corre en el primer montaje, y `online` puede cambiar entre aperturas
  // (p.ej. al marcar el lead como televenta). En online saltamos "modalidad".
  useEffect(() => {
    if (open) {
      setScreen(online ? "slot" : "modalidad");
      setModalidad(online ? "online" : null);
      setDayIso(null);
      setHour(null);
      setBusiness("");
    }
  }, [open, online]);

  if (!open) return null;

  const reset = () => {
    setScreen(online ? "slot" : "modalidad");
    setDayIso(null);
    setHour(null);
    setBusiness("");
    setModalidad(online ? "online" : null);
  };
  const close = () => {
    reset();
    onClose?.();
  };

  // Pantalla anterior para el botón "Atrás" (null = mostrar "Cancelar" que cierra).
  const backTarget =
    screen === "slot" ? (online ? null : "modalidad") : screen === "details" ? "slot" : null;

  const Header = () => (
    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
      {backTarget ? (
        <button
          type="button"
          onClick={() => setScreen(backTarget)}
          className="flex items-center gap-1 text-emerald-600 font-semibold text-sm active:scale-95"
        >
          <ChevronLeft className="w-4 h-4" /> Atrás
        </button>
      ) : (
        <button
          type="button"
          onClick={close}
          className="text-emerald-600 font-semibold text-sm active:scale-95"
        >
          Cancelar
        </button>
      )}
      <div className="w-1/3 h-1.5 bg-slate-200 rounded-full" />
      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
        <MoreHorizontal className="w-4 h-4" />
      </div>
    </div>
  );

  const Footer = () => (
    <div className="flex items-center justify-center gap-1.5 px-4 py-2.5 border-t border-slate-100 shrink-0 text-[11px] text-slate-400">
      <span className="w-4 h-4 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[7px] font-black text-sky-500">
        ✕
      </span>
      Administrado por ConextCRM. <span className="font-bold text-emerald-600">Más información</span>
    </div>
  );

  return (
    <div
      style={{ zIndex: overlayZIndex }}
      className="fixed inset-0 flex items-end sm:items-center justify-center bg-slate-900/70 backdrop-blur-sm sm:p-4"
      onClick={close}
    >
      <motion.div
        initial={{ y: "100%", opacity: 0.6 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 320, damping: 32 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-sm h-[92vh] sm:h-[660px] sm:max-h-[90vh] rounded-t-[1.75rem] sm:rounded-[2rem] shadow-2xl flex flex-col overflow-hidden"
      >
        <Header />

        <div className="flex-1 overflow-y-auto px-5 py-5">
          <AnimatePresence mode="wait">
            {screen === "modalidad" && (
              <motion.div
                key="modalidad"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                className="space-y-5"
              >
                <div>
                  <h3 className="text-lg font-black text-slate-900">¿Cómo prefieres la presentación?</h3>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    Elige cómo quieres que te enseñemos la aplicación. Después escoges el día y la hora.
                  </p>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block">
                    Modalidad
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: "presencial", label: "Presencial" },
                      { id: "online", label: "Online (videollamada)" },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setModalidad(opt.id)}
                        className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all active:scale-95 ${
                          modalidad === opt.id
                            ? "bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-200/50"
                            : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {screen === "slot" && (
              <motion.div
                key="slot"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                className="space-y-5"
              >
                <div>
                  <h3 className="text-lg font-black text-slate-900">
                    {online ? "Reserva tu videollamada online 💻" : "Reserva tu presentación"}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    {online
                      ? `Elige el día y la hora para tu videollamada online con ${vendorName}. Dura unos 30 min aprox.`
                      : `Elige el día y la hora para tu demostración con ${vendorName}. Dura unos 30 min aprox.`}
                  </p>
                </div>

                <div>
                  <label className="flex items-center gap-1.5 text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                    <Calendar className="w-3.5 h-3.5" /> Día
                  </label>
                  <MonthCalendar
                    selected={dayIso}
                    onSelect={(iso) => { setDayIso(iso); setHour(null); }}
                  />
                </div>

                <AnimatePresence>
                  {dayIso && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
                      <label className="flex items-center gap-1.5 text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                        <Clock className="w-3.5 h-3.5" /> Hora
                      </label>
                      <div className="grid grid-cols-4 gap-2">
                        {MOCK_HOURS.map((h) => (
                          <button
                            key={h}
                            type="button"
                            onClick={() => setHour(h)}
                            className={`px-2 py-2 rounded-xl text-xs font-bold border transition-all active:scale-95 ${
                              hour === h
                                ? "bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-200/50"
                                : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"
                            }`}
                          >
                            {h}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {screen === "details" && (
              <motion.div
                key="details"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                className="space-y-5"
              >
                <div>
                  <h3 className="text-lg font-black text-slate-900">Casi listo 🙌</h3>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    Presentación con {vendorName} el <b className="text-slate-700">{fmtEs(dayIso)}</b> a las{" "}
                    <b className="text-slate-700">{hour}</b> (unos 30 min aprox).
                  </p>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block">
                    Nombre del negocio
                  </label>
                  <input
                    type="text"
                    value={business}
                    onChange={(e) => setBusiness(e.target.value)}
                    placeholder="¿Cómo se llama tu negocio?"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none"
                  />
                </div>
              </motion.div>
            )}

            {screen === "success" && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center text-center pt-8 space-y-4"
              >
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                  <CheckCircle2 className="w-9 h-9" />
                </div>
                <h3 className="text-xl font-black text-slate-900">¡Presentación reservada!</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {vendorName} {modalidad === "online" ? "te llamará por videollamada" : "te visitará"} el{" "}
                  <b className="text-slate-700">{fmtEs(dayIso)}</b> a las{" "}
                  <b className="text-slate-700">{hour}</b>.{business ? ` ¡Gracias, ${business}!` : ""}
                </p>
                <div className="flex items-start gap-2 text-left bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3">
                  <MessageCircle className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-emerald-700 font-medium leading-relaxed">
                    Recibirás un WhatsApp con los detalles de la cita y el teléfono de tu comercial por si quieres cambiarla.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Botón de acción (estilo "footer" de pantalla de Flow) */}
        <div className="px-5 pb-3 pt-1 shrink-0">
          {screen === "modalidad" && (
            <button
              type="button"
              disabled={!modalidad}
              onClick={() => setScreen("slot")}
              className="w-full py-3.5 rounded-2xl bg-emerald-500 text-white font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-600 transition-all active:scale-[0.99]"
            >
              Continuar
            </button>
          )}
          {screen === "slot" && (
            <button
              type="button"
              disabled={!dayIso || !hour}
              onClick={() => setScreen("details")}
              className="w-full py-3.5 rounded-2xl bg-emerald-500 text-white font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-600 transition-all active:scale-[0.99]"
            >
              Continuar
            </button>
          )}
          {screen === "details" && (
            <button
              type="button"
              disabled={!business.trim()}
              onClick={() => setScreen("success")}
              className="w-full py-3.5 rounded-2xl bg-emerald-500 text-white font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-600 transition-all active:scale-[0.99]"
            >
              Reservar presentación
            </button>
          )}
          {screen === "success" && (
            <button
              type="button"
              onClick={close}
              className="w-full py-3.5 rounded-2xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-800 transition-all active:scale-[0.99]"
            >
              Finalizar
            </button>
          )}
        </div>

        <Footer />
      </motion.div>
    </div>
  );
}
